import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

// React.lazy keeps plotly.js (+ react-plotly.js/factory) out of the main chunk.
const PlotlyChart = React.lazy(() => import(/* webpackChunkName: "plotly" */ './PlotlyChart'));

function ChartLoading() {
  const { t } = useTranslation();
  return (
    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
      <CircularProgress size={20} />
      <Typography variant="caption" color="text.secondary">{t('Dashboards.widget.loadingChart')}</Typography>
    </Box>
  );
}

/** Catches a failed chunk download or a Plotly render error so one widget can't take down the grid. */
class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    console.error('PlotlyWidget render failed:', error);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      const { fallback } = this.props;
      return typeof fallback === 'function' ? fallback(this.state.error) : fallback;
    }
    return this.props.children;
  }
}

/**
 * Renders a server-compiled Plotly figure (`figure_json`). The figure is never
 * mutated or persisted client-side; viewer-local interactions (zoom, hover,
 * legend toggles) stay inside Plotly.
 *
 * @param figure    `{data, layout, frames?}` from WidgetDataResponse.figure_json
 * @param filename  name used by the modebar's PNG download
 * @param fallback  element rendered if the chart fails (e.g. the table view), or a
 *                  function `(error) => element` so the caller can say why
 * @param onClick   optional plotly_click handler (F2: drill-through / apply as filter)
 */
export default function PlotlyWidget({ figure, filename, fallback = null, onClick }) {
  return (
    <ChartErrorBoundary fallback={fallback} resetKey={figure}>
      <React.Suspense fallback={<ChartLoading />}>
        <PlotlyChart figure={figure} filename={filename} onClick={onClick} />
      </React.Suspense>
    </ChartErrorBoundary>
  );
}
